import { useState } from 'react'

// PlanType enum from the API — the form only ever offered recurring plans,
// so punch cards, drop-ins and packs were unreachable.
const PLAN_TYPES = [
  ['recurring', 'Recurring membership'],
  ['punch_card', 'Punch card'],
  ['drop_in', 'Drop-in'],
  ['hybrid', 'Hybrid'],
  ['event', 'Event'],
  ['appointment_pack', 'Appointment pack'],
]
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  DollarSign,
  Users,
  Shield,
  Globe,
  Lock,
  X,
  Loader2,
  Tag,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { plans } from '../api/endpoints.js'

const EMPTY_PLAN = {
  name: '',
  description: '',
  price: '',
  type: 'recurring',
  visits_included: '',
  billing_cycle: 'monthly',
  gate_access: false,
  is_public: true,
  max_members: '',
  freeze_allowed: false,
  pause_allowed: false,
}

function PlanModal({ plan = null, onClose }) {
  const qc = useQueryClient()
  const isEdit = !!plan
  const [form, setForm] = useState(
    isEdit
      ? {
          ...EMPTY_PLAN,
          ...plan,
          price: String(plan.price ?? ''),
          max_members: String(plan.max_members ?? ''),
        }
      : EMPTY_PLAN,
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name || !form.price) {
      setError('Name and price are required.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
      type: form.type,
      visits_included: form.visits_included === '' ? null : Number(form.visits_included),
        ...form,
        price: parseFloat(form.price),
        max_members: form.max_members ? parseInt(form.max_members) : null,
      }
      if (isEdit) {
        await plans.update(plan.id, payload)
        toast.success('Plan updated!')
      } else {
        await plans.create(payload)
        toast.success('Plan created!')
      }
      qc.invalidateQueries(['plans'])
      onClose()
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save plan.')
    } finally {
      setSaving(false)
    }
  }

  const CYCLES = ['monthly', 'weekly', 'biweekly', 'yearly', 'one-time']

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-surface-2 border border-surface-3 rounded-2xl w-full max-w-lg shadow-2xl animate-fade-in overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-surface-3 sticky top-0 bg-surface-2 z-10">
          <h2 className="text-lg font-semibold text-text-primary">
            {isEdit ? 'Edit Plan' : 'Create Plan'}
          </h2>
          <button onClick={onClose} className="text-text-dim hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <p className="text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <div>
            <label className="label">Plan Name *</label>
            <input
              name="name"
              className="input-field"
              placeholder="e.g. Unlimited Monthly"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea
              name="description"
              className="input-field resize-none"
              rows={2}
              placeholder="Optional description…"
              value={form.description}
              onChange={handleChange}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Price ($) *</label>
              <input
                name="price"
                type="number"
                step="0.01"
                min="0"
                className="input-field"
                placeholder="49.99"
                value={form.price}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="label">Plan type</label>
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className="input-field"
              >
                {PLAN_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {(form.type === 'punch_card' || form.type === 'appointment_pack') && (
              <div>
                <label className="label">Visits included</label>
                <input
                  name="visits_included"
                  type="number"
                  min="1"
                  value={form.visits_included}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            )}
            <div>
              <label className="label">Billing Cycle</label>
              <select
                name="billing_cycle"
                className="input-field"
                value={form.billing_cycle}
                onChange={handleChange}
              >
                {CYCLES.map((c) => (
                  <option key={c} value={c} className="bg-surface-3">
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Max Members (leave blank for unlimited)</label>
            <input
              name="max_members"
              type="number"
              min="1"
              className="input-field"
              placeholder="Unlimited"
              value={form.max_members}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-3 pt-2">
            {[
              { name: 'gate_access', label: 'Gate Access', help: 'Members on this plan can use the gate' },
              { name: 'is_public', label: 'Public Plan', help: 'Visible on public signup page' },
              { name: 'pause_allowed', label: 'Allow Pause', help: 'Members can pause this plan' },
              { name: 'freeze_allowed', label: 'Allow Freeze', help: 'Members can freeze this plan' },
            ].map(({ name, label, help }) => (
              <label key={name} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name={name}
                  checked={!!form[name]}
                  onChange={handleChange}
                  className="mt-0.5 w-4 h-4 accent-primary"
                />
                <div>
                  <p className="text-sm font-medium text-text-primary">{label}</p>
                  <p className="text-xs text-text-muted">{help}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              ) : (
                isEdit ? 'Save Changes' : 'Create Plan'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function PlanCard({ plan, onEdit, onDelete, deleting }) {
  return (
    <div className="card hover:border-surface-4 transition-colors flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-bold text-text-primary text-lg">{plan.name}</h3>
            {plan.gate_access && (
              <span className="badge-active gap-1">
                <Shield className="w-3 h-3" />
                Gate Access
              </span>
            )}
            {plan.is_public ? (
              <span className="badge-info">
                <Globe className="w-3 h-3" />
                Public
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-surface-3 text-text-muted border border-surface-4">
                <Lock className="w-3 h-3" />
                Private
              </span>
            )}
          </div>
          {plan.description && (
            <p className="text-sm text-text-muted">{plan.description}</p>
          )}
        </div>
        <button
          onClick={() => onEdit(plan)}
          className="btn-secondary text-xs shrink-0"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(plan)}
          disabled={deleting}
          title="Delete plan"
          aria-label="Delete plan"
          className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--danger))] hover:bg-[rgb(var(--danger))]/12 transition-all duration-150 active:scale-95 disabled:opacity-40"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="font-bold text-text-primary">
              ${parseFloat(plan.price ?? 0).toFixed(2)}
            </p>
            <p className="text-xs text-text-muted capitalize">
              {plan.billing_cycle ?? 'monthly'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-surface-3 rounded-lg flex items-center justify-center">
            <Users className="w-4 h-4 text-text-muted" />
          </div>
          <div>
            <p className="font-bold text-text-primary">
              {plan.member_count?.toLocaleString() ?? '—'}
            </p>
            <p className="text-xs text-text-muted">members</p>
          </div>
        </div>
        {plan.max_members && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-surface-3 rounded-lg flex items-center justify-center">
              <Tag className="w-4 h-4 text-text-muted" />
            </div>
            <div>
              <p className="font-bold text-text-primary">{plan.max_members}</p>
              <p className="text-xs text-text-muted">max</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function PlansPage() {
  const [showModal, setShowModal] = useState(false)
  const [editPlan, setEditPlan] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['plans'],
    queryFn: () => plans.list().then((r) => r.data),
  })

  const planList = Array.isArray(data) ? data : data?.items ?? []

  const removePlan = useMutation({
    mutationFn: (id) => plans.remove(id),
    onMutate: (id) => setDeletingId(id),
    onSuccess: () => {
      toast.success('Plan deleted')
      qc.invalidateQueries({ queryKey: ['plans'] })
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not delete plan'),
    onSettled: () => setDeletingId(null),
  })

  const handleDelete = (plan) => {
    if (plan.enrollment_count > 0) {
      toast.error(`${plan.name} has ${plan.enrollment_count} enrolment(s) — cancel those first.`)
      return
    }
    if (window.confirm(`Delete "${plan.name}"? This cannot be undone.`)) {
      removePlan.mutate(plan.id)
    }
  }

  const handleEdit = (plan) => {
    setEditPlan(plan)
    setShowModal(true)
  }

  const handleClose = () => {
    setShowModal(false)
    setEditPlan(null)
  }

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Plans</h1>
          {planList.length > 0 && (
            <p className="text-text-muted text-sm mt-0.5">
              {planList.length} plan{planList.length !== 1 ? 's' : ''} configured
            </p>
          )}
        </div>
        <button
          onClick={() => { setEditPlan(null); setShowModal(true) }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Plan
        </button>
      </div>

      {error && (
        <p className="text-sm" style={{ color: 'rgb(var(--danger))' }}>Failed to load plans.</p>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card">
              <div className="h-5 w-1/2 bg-surface-3 rounded animate-pulse mb-3" />
              <div className="h-4 w-3/4 bg-surface-3 rounded animate-pulse mb-2" />
              <div className="h-4 w-1/3 bg-surface-3 rounded animate-pulse" />
            </div>
          ))}
        </div>
      ) : planList.length === 0 ? (
        <div className="card text-center py-16 text-text-dim">
          <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No plans yet. Create your first membership plan.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {planList.map((p) => (
            <PlanCard key={p.id} plan={p} onEdit={handleEdit} onDelete={handleDelete} deleting={deletingId === p.id} />
          ))}
        </div>
      )}

      {showModal && (
        <PlanModal plan={editPlan} onClose={handleClose} />
      )}
    </div>
  )
}
